/**
 * VoiceButton — the single microphone across Vault (web). Four states from the
 * Voice & AI spec: Idle, Listening, Transcribing, Error/no-permission. It sits
 * beside a text input and never replaces it — voice is a convenience, and every
 * failure path leaves typed input untouched.
 *
 * Two capture paths, best-first: when the browser has the native Web Speech API
 * it streams — a LIVE caption shows while listening and the field fills word by
 * word (interim results, mirroring Guide.jsx). Where that is missing it records
 * a clip and POSTs to /budget/places/transcribe/ instead. Either way text comes
 * back via onText; onState reports the state to a host, and the button renders
 * its own caption / error line so all hosts get it for free.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useRef, useState } from 'react';
import { api } from '../api.js';
import { AskEnableAI, ensureAIConsent, useAI } from './AIGate.jsx';
import { useAuth } from '../App.jsx';

const BASE = {
  borderRadius: '50%', display: 'flex', alignItems: 'center',
  justifyContent: 'center', border: '1px solid', flexShrink: 0,
  background: 'transparent', cursor: 'pointer', transition: 'all .15s',
};
const STYLE = {
  idle: { borderColor: 'var(--color-divider)', color: 'color-mix(in srgb, var(--color-text) 45%, transparent)' },
  listening: { borderColor: 'var(--color-accent)', background: 'color-mix(in srgb, var(--color-accent) 22%, transparent)', color: 'var(--color-accent)', boxShadow: '0 0 0 6px color-mix(in srgb, var(--color-accent) 12%, transparent)' },
  transcribing: { borderColor: 'var(--color-accent-700)', color: 'var(--color-accent-400)' },
  error: { borderColor: 'var(--color-warn)', color: 'var(--color-warn)' },
};
const GLYPH = { idle: '🎤', listening: '■', transcribing: '…', error: '🎤' };
export const VOICE_ERROR = 'Microphone is off — type it instead.';

export default function VoiceButton({ onText, onState, size = 44, title }) {
  const [state, setState] = useState('idle'); // idle | listening | transcribing | error
  const [caption, setCaption] = useState('');  // live interim transcript while listening
  const rec = useRef(null);   // MediaRecorder — record-and-POST fallback path
  const sr = useRef(null);    // SpeechRecognition — native streaming path
  const chunks = useRef([]);
  const set = (s) => { setState(s); onState?.(s); };
  const ai = useAI();
  const { me } = useAuth() || {};
  // The browser's own speech recognition is not Vault's AI and keeps working;
  // only the server transcription fallback obeys the AI master switch.
  const hasNative = typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Native streaming (Web Speech API): continuous with interim results, so the
  // caption updates live and the field fills as the words land. Returns false
  // when the browser has no SpeechRecognition, so start() falls back to record.
  const startNative = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;
    let finalText = '';
    const r = new SR();
    r.lang = 'en-ZA'; r.continuous = true; r.interimResults = true;
    r.onresult = (ev) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t; else interim += t;
      }
      const live = (finalText + interim).trim();
      setCaption(live);
      if (live) onText?.(live);   // interim streaming — the field fills as we speak
    };
    r.onerror = (e) => {
      // No mic permission → a visible line, not just a tooltip.
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') set('error');
      else set('idle');
      setCaption('');
    };
    r.onend = () => {
      sr.current = null;
      if (finalText.trim()) onText?.(finalText.trim());
      setCaption(''); set('idle');
    };
    try { r.start(); } catch { return false; }
    sr.current = r; set('listening'); return true;
  };

  // Fallback: record a short clip and POST it to the server transcriber.
  const startRecord = async () => {
    // Apple 5.1.1(i): the clip goes to Gemini, so ask before the mic opens.
    if (!(await ensureAIConsent(me))) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        set('transcribing');
        try {
          const fd = new FormData();
          fd.append('audio', new Blob(chunks.current, { type: 'audio/webm' }), 'voice.webm');
          const d = await api('/budget/places/transcribe/', { method: 'POST', form: fd });
          if (d?.text) onText?.(d.text);
        } catch { /* silent — typing still works */ }
        set('idle');
      };
      mr.start(); rec.current = mr; set('listening');
    } catch { set('error'); }   // permission/mic denied → visible error line
  };

  const start = () => { setCaption(''); if (!startNative()) startRecord(); };
  const stop = () => {
    try { sr.current?.stop(); } catch { /* ignore */ }
    try { rec.current?.stop(); } catch { /* ignore */ }
    rec.current = null;
  };

  const style = { ...BASE, width: size, height: size, fontSize: Math.round(size * 0.42), ...STYLE[state] };
  if (!hasNative && ai.hidden) return null;
  if (!hasNative && ai.disabled) {
    return (
      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, flexDirection: 'column', alignItems: 'center' }}>
        <button type="button" disabled style={{ ...style, ...STYLE.idle, opacity: 0.4, cursor: 'not-allowed' }}
                aria-label="Voice needs AI, which is switched off" title="Voice needs AI, which is switched off">
          {GLYPH.idle}
        </button>
        <AskEnableAI feature="voice input" />
      </span>
    );
  }
  const showLine = state === 'error' || (state === 'listening' && caption);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button type="button" style={style}
              className={state === 'listening' ? 'animate-pulse' : ''}
              aria-label={title || 'Dictate by voice'}
              title={state === 'error' ? VOICE_ERROR : (title || 'Dictate by voice')}
              onClick={state === 'listening' ? stop : state === 'transcribing' ? undefined : start}>
        {GLYPH[state]}
      </button>
      {showLine && (
        <span aria-live="polite"
              style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 20,
                maxWidth: 260, fontSize: 11, lineHeight: 1.3, pointerEvents: 'none',
                padding: '2px 7px', borderRadius: 8,
                background: 'var(--color-surface, #1c1f31)',
                border: '1px solid var(--color-divider)',
                color: state === 'error' ? 'var(--color-warn)' : 'var(--color-accent-400)',
              }}>
        {state === 'error' ? VOICE_ERROR : caption}
        </span>
      )}
    </span>
  );
}
